import { index, pgTable, text, timestamp } from 'drizzle-orm/pg-core'

import { createdAt, primaryId } from './columns.ts'

/**
 * A rehearsal of the whole shop, thrown away when it ends (owner's decision).
 *
 * > „Admin będzie robił testowe zamówienia żeby pokazać pracownikowi jak to
 * > wygląda — całe takie szkolenie potrwa 20 minut i potem wszystko admin
 * > usuwa."
 *
 * ── WHY A ROW AND NOT A FLAG ─────────────────────────────────────────────
 * The obvious cheaper thing was a boolean — `order.is_test` — and it fails on
 * the half of the request that matters: „po kliknięciu »Zakończ test« system ma
 * jednym działaniem usunąć wszystkie dane wygenerowane przez ten test". A flag
 * can mark an order. It cannot say which invented customer, which invented
 * performer and which of three orders belonged to the SAME twenty minutes, so
 * tearing one rehearsal down while another is running would be a query nobody
 * can write correctly twice.
 *
 * This row is the handle. Everything a rehearsal creates points at it with
 * `ON DELETE CASCADE`, so „Zakończ test" is one `DELETE` against one row and
 * the database does the rest — including the tables nobody remembered, which is
 * exactly the class of leftover the owner asked not to have.
 *
 * ── THE ONE THING IT MUST NEVER BE ───────────────────────────────────────
 * A dev-only toy. `pnpm dev` on this repo talks to the LIVE database — there is
 * no local copy — so a rehearsal's rows land next to real orders the moment
 * they are written. That is why the marking is a foreign key rather than a
 * convention, why {@link isTrainingOrder} is asked before any mail, payment or
 * invoice leaves the building, and why the teardown is verified rather than
 * assumed.
 *
 * ── NO FOREIGN KEY ON `startedByUserId`, ON PURPOSE ──────────────────────
 * `user.training_session_id` points HERE, so a reference back to `user` would
 * make the two modules import each other and drizzle-kit resolve a cycle it has
 * no reason to resolve. The admin who started a rehearsal is recorded by id and
 * by a name snapshot instead: the screen shows the snapshot, and the id is only
 * ever compared, never joined. A rehearsal outlives nobody — twenty minutes —
 * so the usual argument for a live join does not apply.
 */
export const trainingSession = pgTable(
  'training_session',
  {
    id: primaryId('tst'),

    /** What this rehearsal is for, in the admin's words. Shown in the list. */
    label: text('label').notNull(),

    /** The admin who started it. Compared, never joined — see the note above. */
    startedByUserId: text('started_by_user_id').notNull(),
    /** Their name as it read when they started, so the list needs no join. */
    startedByName: text('started_by_name'),

    startedAt: timestamp('started_at', { withTimezone: true }).notNull().defaultNow(),

    /**
     * Set the moment „Zakończ test" runs, and then the row is deleted.
     *
     * It exists for the window in between: the teardown deletes a dozen tables
     * and a rehearsal that is being dismantled must stop accepting new orders
     * first, or the last insert races the last delete and leaves the leftover
     * this whole table is here to prevent. A session with `endedAt` set is
     * closed to writes and on its way out.
     */
    endedAt: timestamp('ended_at', { withTimezone: true }),

    createdAt: createdAt(),
  },
  (t) => [index('training_session_started_by_idx').on(t.startedByUserId, t.startedAt)],
)

export type TrainingSession = typeof trainingSession.$inferSelect
